// src/components/GeneradorPDF.js

import { useEffect, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Cookies from "js-cookie";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import { getAuthToken } from "../utils/session";

const BRAND = {
  company: "DQPA",
  title: "Informe Ejecutivo de Autoevaluación",
  subtitle: "Gobierno, Gestión y Calidad del Dato",
  classification: "Documento ejecutivo confidencial",
  framework: "UNE 0077 · UNE 0078 · UNE 0079 · UNE 0080",
};

const COLORS = {
  ink: "#111827",
  graphite: "#1F2937",
  slate: "#334155",
  muted: "#64748B",
  line: "#D7DEE8",
  lineSoft: "#E8EDF3",
  paper: "#FFFFFF",
  canvas: "#F6F8FB",
  canvas2: "#EEF2F7",
  executive: "#0B1F33",
  executive2: "#152C46",
  steel: "#3F5F7F",
  blueGray: "#EAF0F6",
  champagne: "#BFA46A",
  champagneSoft: "#F4EFE3",
  success: "#1F7A4D",
  successSoft: "#EAF4EF",
  warning: "#9A6B21",
  warningSoft: "#F8F1E3",
  danger: "#9F2F2F",
  dangerSoft: "#F5EAEA",
  white: "#FFFFFF",
  black: "#000000",
};

const LEVEL_COLORS = {
  N: COLORS.danger,
  P: "#A45B35",
  A: COLORS.warning,
  T: COLORS.success,
};

const STATIC_EXECUTIVE_TEXT =
  "El presente informe sintetiza los resultados de la autoevaluación de capacidad de los procesos de datos realizada conforme al marco UNE 0080:2023. El análisis integra los ámbitos de gobierno, gestión y calidad del dato, tomando como referencia los procesos definidos en las UNE 0077, UNE 0078 y UNE 0079. Su finalidad es ofrecer una lectura ejecutiva, trazable y accionable del estado actual, las principales brechas de madurez y las iniciativas prioritarias para evolucionar hacia un modelo de gestión del dato más robusto, gobernado y sostenible.";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  return String(value);
};

const cleanText = (value) =>
  safeString(value)
    .replace(/\*\*/g, "")
    .replace(/\r/g, "")
    .replace(/\t/g, " ")
    .replace(/[ ]{2,}/g, " ")
    .trim();

const formatDate = (date = new Date()) =>
  new Date(date).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

const hexToRgb = (hex) => {
  const sanitized = String(hex || "#000000").replace("#", "");
  const bigint = parseInt(sanitized, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const getLevelStatus = (score) => {
  const value = Number(score);

  if (Number.isNaN(value)) {
    return {
      text: "-",
      short: "-",
      label: "Sin dato",
      color: COLORS.muted,
    };
  }

  if (value <= 15) {
    return {
      text: `${value.toFixed(2)}% (N)`,
      short: "N",
      label: "No logrado",
      color: LEVEL_COLORS.N,
    };
  }

  if (value <= 50) {
    return {
      text: `${value.toFixed(2)}% (P)`,
      short: "P",
      label: "Parcialmente logrado",
      color: LEVEL_COLORS.P,
    };
  }

  if (value <= 85) {
    return {
      text: `${value.toFixed(2)}% (A)`,
      short: "A",
      label: "Ampliamente logrado",
      color: LEVEL_COLORS.A,
    };
  }

  return {
    text: `${value.toFixed(2)}% (T)`,
    short: "T",
    label: "Totalmente logrado",
    color: LEVEL_COLORS.T,
  };
};

const normalizeTaskId = (task) => task?.id || task?._id || task?.tareaId || task?.respuestaId || task?.tarea || task?.titulo || "";

const normalizeForMatch = (value) =>
  cleanText(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const extractTitleFromDescriptionForPdf = (text) => {
  const value = cleanText(text);
  if (!value) return "";

  const patterns = [
    /Consiste en\s+(.+?)(?:,\s+asegurando|\s+asegurando|\.\s+Desde|\.\s+Su prioridad|$)/i,
    /Las tareas? prioritarias? (?:son|es):\s+(.+?)(?:\.|$)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return cleanText(match[1]);
  }

  return "";
};

const getTaskTitleForMatching = (task) => {
  if (typeof task === "string") return cleanText(task);
  return cleanText(
    task?.tituloCompleto ||
      task?.tituloOriginal ||
      task?.tareaOriginal ||
      task?.tarea ||
      task?.titulo ||
      task?.nombre ||
      task?.name ||
      task?.title ||
      task?.texto ||
      extractTitleFromDescriptionForPdf(task?.descripcion) ||
      extractTitleFromDescriptionForPdf(task?.descripcionCompleta)
  );
};

const getTaskProcessForMatching = (task) =>
  cleanText(task?.procesoNombre || task?.proceso || task?.nombreProceso || task?.processName || "");

const firstTaskStep = (task) =>
  safeArray(task?.pasos || task?.steps)
    .map(cleanText)
    .filter(Boolean)[0] || "";

const getTaskMatchKeyForPdf = (task) =>
  normalizeForMatch([
    getTaskTitleForMatching(task),
    getTaskProcessForMatching(task),
    firstTaskStep(task).slice(0, 120),
  ].filter(Boolean).join(" | "));

const mergeRoadmapTaskWithCatalog = (roadmapTask, catalogTasks = []) => {
  const candidates = safeArray(catalogTasks);
  const roadmapIds = [
    roadmapTask?._id,
    roadmapTask?.id,
    roadmapTask?.tareaId,
    roadmapTask?.respuestaId,
    roadmapTask?.respuestaSeleccionadaId,
  ].filter(Boolean).map(String);

  const roadmapTitle = normalizeForMatch(getTaskTitleForMatching(roadmapTask));
  const roadmapProcess = normalizeForMatch(getTaskProcessForMatching(roadmapTask));
  const roadmapDescription = normalizeForMatch(roadmapTask?.descripcion || roadmapTask?.descripcionCompleta);
  const roadmapFirstStep = normalizeForMatch(firstTaskStep(roadmapTask));
  const roadmapKey = getTaskMatchKeyForPdf(roadmapTask);

  let best = null;
  let bestScore = -1;

  for (const candidate of candidates) {
    const candidateIds = [
      candidate?._id,
      candidate?.id,
      candidate?.tareaId,
      candidate?.respuestaId,
      candidate?.respuestaSeleccionadaId,
    ].filter(Boolean).map(String);

    let score = 0;
    if (roadmapIds.length && roadmapIds.some((id) => candidateIds.includes(id))) score += 100;

    const candidateKey = getTaskMatchKeyForPdf(candidate);
    if (roadmapKey && candidateKey && roadmapKey === candidateKey) score += 80;

    const candidateTitle = normalizeForMatch(getTaskTitleForMatching(candidate));
    if (roadmapTitle && candidateTitle) {
      if (roadmapTitle === candidateTitle) score += 55;
      else if (roadmapTitle.length > 20 && candidateTitle.includes(roadmapTitle.slice(0, 35))) score += 25;
      else if (candidateTitle.length > 20 && roadmapTitle.includes(candidateTitle.slice(0, 35))) score += 25;
    }

    const candidateProcess = normalizeForMatch(getTaskProcessForMatching(candidate));
    if (roadmapProcess && candidateProcess && roadmapProcess === candidateProcess) score += 18;

    const candidateDescription = normalizeForMatch(candidate?.descripcion || candidate?.descripcionCompleta);
    if (roadmapDescription && candidateDescription) {
      if (roadmapDescription === candidateDescription) score += 35;
      else if (roadmapDescription.length > 90 && candidateDescription.includes(roadmapDescription.slice(0, 90))) score += 18;
      else if (candidateDescription.length > 90 && roadmapDescription.includes(candidateDescription.slice(0, 90))) score += 18;
    }

    const candidateFirstStep = normalizeForMatch(firstTaskStep(candidate));
    if (roadmapFirstStep && candidateFirstStep && roadmapFirstStep === candidateFirstStep) score += 30;

    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  if (!best || bestScore < 25) return roadmapTask;

  return {
    ...best,
    ...roadmapTask,
    // Campos operativos del catálogo detallado: son la fuente buena para el PDF.
    titulo: getTaskTitleForMatching(best) || getTaskTitleForMatching(roadmapTask),
    tarea: getTaskTitleForMatching(best) || getTaskTitleForMatching(roadmapTask),
    descripcion: best?.descripcion || roadmapTask?.descripcion,
    pasos: safeArray(best?.pasos).length ? best.pasos : safeArray(roadmapTask?.pasos),
    evidencias: safeArray(best?.evidencias).length ? best.evidencias : safeArray(roadmapTask?.evidencias),
    responsablesSugeridos: safeArray(best?.responsablesSugeridos).length
      ? best.responsablesSugeridos
      : safeArray(roadmapTask?.responsablesSugeridos || roadmapTask?.responsables || roadmapTask?.implicados),
    procesoNombre: best?.procesoNombre || roadmapTask?.procesoNombre || best?.proceso || roadmapTask?.proceso,
    proceso: best?.procesoNombre || roadmapTask?.procesoNombre || best?.proceso || roadmapTask?.proceso,
    procesoTipo: best?.procesoTipo || roadmapTask?.procesoTipo || best?.tipoProceso || roadmapTask?.tipoProceso,
  };
};

const isBadGeneratedDescription = (description) => {
  const value = cleanText(description).toLowerCase();

  if (!value) return true;

  return (
    value.includes("descripción no generada") ||
    value.includes("descripcion no generada") ||
    value.includes("error interno") ||
    value.includes("internal error") ||
    value === "null" ||
    value === "undefined"
  );
};

class PDFBuilder {
  constructor(doc, data, aiOverview, chartImages, tasks) {
    this.doc = doc;
    this.data = data || {};
    this.aiOverview = cleanText(aiOverview);
    this.chartImages = chartImages || {};
    this.tasks = safeArray(tasks);

    this.FONT = "helvetica";

    this.PAGE_WIDTH = this.doc.internal.pageSize.getWidth();
    this.PAGE_HEIGHT = this.doc.internal.pageSize.getHeight();

    this.MARGIN_X = 12;
    this.TOP_MARGIN = 22;
    this.BOTTOM_MARGIN = 14;
    this.CONTENT_WIDTH = this.PAGE_WIDTH - this.MARGIN_X * 2;

    this.y = this.TOP_MARGIN;
    this.tocEntries = [];
    this.currentSection = "";
  }

  generate() {
    this.addCoverPage();
    this.addTocPage();

    this.addExecutiveSummarySection();
    this.addGraphicOverviewSection();
    this.addConsolidatedResultsSection();
    this.addDetailedProcessSection();
    this.addGeneralQuestionsSection();
    this.addImprovementPlanSection();
    this.addClosingPage();

    this.renderTableOfContents();
    this.addHeadersAndFooters();
  }

  setTextColor(hex) {
    this.doc.setTextColor(...hexToRgb(hex));
  }

  setFillColor(hex) {
    this.doc.setFillColor(...hexToRgb(hex));
  }

  setDrawColor(hex) {
    this.doc.setDrawColor(...hexToRgb(hex));
  }

  setFont(style = "normal", size = 10) {
    this.doc.setFont(this.FONT, style);
    this.doc.setFontSize(size);
  }

  addPage() {
    this.doc.addPage();
    this.y = this.TOP_MARGIN;
  }

  checkPageBreak(requiredHeight = 20) {
    if (this.y + requiredHeight > this.PAGE_HEIGHT - this.BOTTOM_MARGIN) {
      this.addPage();
    }
  }

  getTextHeight(text, width, fontSize = 10, lineHeight = 1.35) {
    const lines = this.doc.splitTextToSize(cleanText(text), width);
    return lines.length * fontSize * 0.36 * lineHeight;
  }

  writeWrappedText(text, x, y, width, options = {}) {
    const {
      fontSize = 10,
      style = "normal",
      color = COLORS.graphite,
      lineHeight = 1.4,
      align = "left",
    } = options;

    const content = cleanText(text);
    if (!content) return y;

    this.setFont(style, fontSize);
    this.setTextColor(color);

    const lines = this.doc.splitTextToSize(content, width);

    this.doc.text(lines, x, y, {
      maxWidth: width,
      lineHeightFactor: lineHeight,
      align,
    });

    return y + lines.length * fontSize * 0.36 * lineHeight + 3;
  }

  writeJustifiedParagraph(text, x, y, width, options = {}) {
    const {
      fontSize = 9.7,
      style = "normal",
      color = COLORS.graphite,
      lineHeight = 1.48,
    } = options;

    const content = cleanText(text);
    if (!content) return y;

    this.setFont(style, fontSize);
    this.setTextColor(color);

    const paragraphs = content.split("\n").filter(Boolean);
    let currentY = y;

    paragraphs.forEach((paragraph) => {
      const words = paragraph.split(/\s+/).filter(Boolean);
      const lines = [];
      let line = "";

      words.forEach((word) => {
        const testLine = line ? `${line} ${word}` : word;
        if (this.doc.getTextWidth(testLine) > width && line) {
          lines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      });

      if (line) lines.push(line);

      lines.forEach((lineText, index) => {
        this.checkPageBreak(fontSize * 0.55);

        const isLastLine = index === lines.length - 1;
        const lineWords = lineText.split(/\s+/).filter(Boolean);

        if (isLastLine || lineWords.length <= 1) {
          this.doc.text(lineText, x, currentY);
        } else {
          const wordsWidth = lineWords.reduce(
            (sum, word) => sum + this.doc.getTextWidth(word),
            0
          );
          const extraSpace = Math.max(0, (width - wordsWidth) / (lineWords.length - 1));

          let currentX = x;
          lineWords.forEach((word) => {
            this.doc.text(word, currentX, currentY);
            currentX += this.doc.getTextWidth(word) + extraSpace;
          });
        }

        currentY += fontSize * 0.36 * lineHeight;
      });

      currentY += fontSize * 0.3;
    });

    return currentY + 2;
  }

  drawThinRule(y, color = COLORS.lineSoft) {
    this.setDrawColor(color);
    this.doc.setLineWidth(0.25);
    this.doc.line(this.MARGIN_X, y, this.PAGE_WIDTH - this.MARGIN_X, y);
  }

  startSection(title, eyebrow = "") {
    this.addPage();
    this.currentSection = title;

    const page = this.doc.internal.getNumberOfPages();
    this.tocEntries.push({ title, page, y: this.y });

    this.setFillColor(COLORS.executive);
    this.doc.rect(0, 0, 5.5, this.PAGE_HEIGHT, "F");

    if (eyebrow) {
      this.setFont("bold", 8.4);
      this.setTextColor(COLORS.champagne);
      this.doc.text(eyebrow.toUpperCase(), this.MARGIN_X, this.y);
      this.y += 10;
    }

    this.setFont("bold", 15.2);
    this.setTextColor(COLORS.executive);
    const titleLines = this.doc.splitTextToSize(title, this.CONTENT_WIDTH - 12);
    this.doc.text(titleLines, this.MARGIN_X, this.y, { lineHeightFactor: 1.2 });

    this.y += titleLines.length * 7.4 + 8;

    this.setDrawColor(COLORS.champagne);
    this.doc.setLineWidth(0.55);
    this.doc.line(this.MARGIN_X, this.y, this.MARGIN_X + 34, this.y);

    this.setDrawColor(COLORS.lineSoft);
    this.doc.setLineWidth(0.25);
    this.doc.line(this.MARGIN_X + 38, this.y, this.PAGE_WIDTH - this.MARGIN_X, this.y);

    this.y += 17;
  }

  addCoverPage() {
    const centerX = this.PAGE_WIDTH / 2;

    this.setFillColor(COLORS.executive);
    this.doc.rect(0, 0, this.PAGE_WIDTH, this.PAGE_HEIGHT, "F");

    this.setFillColor("#0F263E");
    this.doc.rect(0, 0, this.PAGE_WIDTH, 78, "F");

    this.setFillColor(COLORS.champagne);
    this.doc.rect(0, 78, this.PAGE_WIDTH, 1.4, "F");

    this.setFont("bold", 10.5);
    this.setTextColor(COLORS.white);
    this.doc.text(BRAND.company, this.MARGIN_X, 24);

    this.setFont("normal", 7.2);
    this.setTextColor("#CAD5E2");
    this.doc.text(BRAND.classification.toUpperCase(), this.PAGE_WIDTH - this.MARGIN_X, 24, {
      align: "right",
    });

    this.setFont("normal", 7.5);
    this.setTextColor("#AEBBCC");
    this.doc.text(BRAND.framework, this.PAGE_WIDTH - this.MARGIN_X, 31, {
      align: "right",
    });

    let y = 104;

    this.setFont("bold", 28);
    this.setTextColor(COLORS.white);
    this.doc.text("Informe Ejecutivo", this.MARGIN_X, y);

    y += 13;

    this.setFont("normal", 18);
    this.setTextColor("#DCE5EF");
    this.doc.text("Autoevaluación de procesos de datos", this.MARGIN_X, y);

    y += 15;

    this.setFont("normal", 9.5);
    this.setTextColor("#B8C4D2");
    const subtitle =
      "Diagnóstico de capacidad, brechas de madurez y plan de mejora para gobierno, gestión y calidad del dato.";
    const subtitleLines = this.doc.splitTextToSize(subtitle, this.CONTENT_WIDTH - 38);
    this.doc.text(subtitleLines, this.MARGIN_X, y, { lineHeightFactor: 1.55 });

    y += 40;

    this.setDrawColor("#51657A");
    this.doc.setLineWidth(0.25);
    this.doc.line(this.MARGIN_X, y, this.PAGE_WIDTH - this.MARGIN_X, y);

    y += 16;

    const metaW = 78;
    const metaGap = 8;
    this.drawCoverMetaBox(this.MARGIN_X, y, metaW, "FECHA DEL INFORME", formatDate());
    this.drawCoverMetaBox(
      this.MARGIN_X + metaW + metaGap,
      y,
      metaW,
      "MARCO DE REFERENCIA",
      "UNE 0080:2023"
    );

    y += 43;

    this.setFont("bold", 8);
    this.setTextColor(COLORS.champagne);
    this.doc.text("ALCANCE DEL DOCUMENTO", this.MARGIN_X, y);

    y += 9;

    const bullets = [
      "Evaluación de capacidad de procesos de datos.",
      "Lectura ejecutiva del estado actual y brechas principales.",
      "Resultados consolidados por proceso y niveles de capacidad.",
      "Plan de mejora estructurado para su seguimiento operativo.",
    ];

    this.setFont("normal", 8.4);
    this.setTextColor("#D5DEEA");

    bullets.forEach((bullet) => {
      this.setFillColor(COLORS.champagne);
      this.doc.circle(this.MARGIN_X + 1.5, y - 1.8, 0.8, "F");
      this.doc.text(bullet, this.MARGIN_X + 7, y, { maxWidth: this.CONTENT_WIDTH - 15 });
      y += 7.4;
    });

    this.setFont("normal", 7.2);
    this.setTextColor("#91A0B3");
    this.doc.text("Generado automáticamente por la herramienta DQPA", centerX, this.PAGE_HEIGHT - 21, {
      align: "center",
    });

    this.setDrawColor("#51657A");
    this.doc.line(this.MARGIN_X, this.PAGE_HEIGHT - 16, this.PAGE_WIDTH - this.MARGIN_X, this.PAGE_HEIGHT - 16);
  }

  drawCoverMetaBox(x, y, width, label, value) {
    this.setDrawColor("#40566F");
    this.setFillColor("#142D48");
    this.doc.roundedRect(x, y, width, 27, 2.5, 2.5, "FD");

    this.setFont("bold", 6.7);
    this.setTextColor("#9DABC0");
    this.doc.text(label, x + 5, y + 8);

    this.setFont("bold", 10);
    this.setTextColor(COLORS.white);
    this.doc.text(value, x + 5, y + 18, { maxWidth: width - 10 });
  }

  addTocPage() {
    this.doc.addPage();
  }

  renderTableOfContents() {
    this.doc.setPage(2);
    this.y = this.TOP_MARGIN;

    this.setFillColor(COLORS.executive);
    this.doc.rect(0, 0, 5.5, this.PAGE_HEIGHT, "F");

    this.setFont("bold", 18);
    this.setTextColor(COLORS.executive);
    this.doc.text("Índice del informe", this.MARGIN_X, this.y);

    this.y += 7;
    this.drawThinRule(this.y, COLORS.lineSoft);
    this.y += 13;

    this.setFont("normal", 9.5);
    this.setTextColor(COLORS.muted);
    this.doc.text(
      "Estructura de navegación del documento ejecutivo.",
      this.MARGIN_X,
      this.y
    );

    this.y += 16;

    this.tocEntries.forEach((entry, index) => {
      const y = this.y + index * 10.2;
      const number = String(index + 1).padStart(2, "0");
      const title = entry.title.replace(/^\d+\.\s*/, "");
      const page = String(entry.page);

      this.setFont("bold", 8.2);
      this.setTextColor(COLORS.champagne);
      this.doc.text(number, this.MARGIN_X, y);

      this.setFont("normal", 9.4);
      this.setTextColor(COLORS.graphite);
      this.doc.text(title, this.MARGIN_X + 12, y);

      this.setFont("bold", 9.2);
      this.setTextColor(COLORS.executive);
      this.doc.text(page, this.PAGE_WIDTH - this.MARGIN_X, y, { align: "right" });

      const startDotsX = this.MARGIN_X + 12 + this.doc.getTextWidth(title) + 5;
      const endDotsX = this.PAGE_WIDTH - this.MARGIN_X - this.doc.getTextWidth(page) - 5;

      this.setDrawColor(COLORS.line);
      this.doc.setLineDashPattern([0.7, 1.7], 0);
      this.doc.line(startDotsX, y - 1, endDotsX, y - 1);
      this.doc.setLineDashPattern([], 0);

      this.doc.link(this.MARGIN_X, y - 5, this.CONTENT_WIDTH, 7, {
        pageNumber: entry.page,
        top: Math.max(0, entry.y - 10),
      });
    });
  }

  addHeadersAndFooters() {
    const pageCount = this.doc.internal.getNumberOfPages();

    for (let page = 2; page <= pageCount; page += 1) {
      this.doc.setPage(page);

      this.setFont("normal", 7);
      this.setTextColor(COLORS.muted);
      this.doc.text(BRAND.title, this.MARGIN_X, 10.5);

      this.setFont("normal", 7);
      this.setTextColor(COLORS.muted);
      this.doc.text(BRAND.company, this.PAGE_WIDTH - this.MARGIN_X, 10.5, {
        align: "right",
      });

      this.setDrawColor(COLORS.lineSoft);
      this.doc.setLineWidth(0.25);
      this.doc.line(this.MARGIN_X, 14.5, this.PAGE_WIDTH - this.MARGIN_X, 14.5);

      this.setFont("normal", 7);
      this.setTextColor(COLORS.muted);
      this.doc.text(
        `${BRAND.classification} · Página ${page} de ${pageCount}`,
        this.PAGE_WIDTH / 2,
        this.PAGE_HEIGHT - 9,
        { align: "center" }
      );
    }
  }

  parseAiOverview(text) {
    const fallback = {
      introduction:
        "No se dispone de un resumen ejecutivo generado por inteligencia artificial para esta ejecución. El informe se genera igualmente con los resultados estructurados de la autoevaluación, el detalle por proceso y el plan de mejora disponible.",
      strengths: [],
      weaknesses: [],
      available: false,
    };

    const content = cleanText(text);
    if (!content) return fallback;

    const fortRegex = /(Fortalezas\s*(?:Clave|Críticas|a\s+Destacar|Identificadas)*:)/i;
    const weakRegex = /(Debilidades\s*(?:Críticas|a\s+Mejorar|Identificadas)*:)/i;

    const fortMatch = content.match(fortRegex);
    const weakMatch = content.match(weakRegex);

    let introduction = content;
    let strengthsText = "";
    let weaknessesText = "";

    if (fortMatch) {
      introduction = content.split(fortMatch[0])[0].trim();
      strengthsText = content.split(fortMatch[0])[1] || "";

      if (weakMatch && strengthsText.includes(weakMatch[0])) {
        strengthsText = strengthsText.split(weakMatch[0])[0];
      }
    }

    if (weakMatch) {
      if (!fortMatch) introduction = content.split(weakMatch[0])[0].trim();
      weaknessesText = content.split(weakMatch[0])[1] || "";
    }

    const toItems = (block) =>
      cleanText(block)
        .split("\n")
        .map((item) =>
          item
            .trim()
            .replace(/^[-*•]\s*/, "")
            .replace(/^\d+[.)]\s*/, "")
            .trim()
        )
        .filter((item) => item.length > 0 && !fortRegex.test(item) && !weakRegex.test(item));

    return {
      introduction:
        introduction ||
        "El análisis As-Is no incluye una introducción específica. Se presentan a continuación los elementos disponibles.",
      strengths: toItems(strengthsText),
      weaknesses: toItems(weaknessesText),
      available: true,
    };
  }

  addExecutiveSummarySection() {
    this.startSection("1. Resumen ejecutivo", "visión de dirección");

    const procesos = safeArray(this.data.resultadosPorProceso);
    const tasksCount = this.tasks.filter((task) => cleanText(this.getTaskTitleForPdf(task))).length;
    const evaluatedCount = procesos.length;
    const processCounts = this.getProcessCountsByDomain(procesos);

    this.addExecutiveKpiStrip([
      {
        label: "Procesos de Gobierno",
        value: processCounts.gobierno,
        note: "Procesos evaluados",
      },
      {
        label: "Procesos de Gestión",
        value: processCounts.gestion,
        note: "Procesos evaluados",
      },
      {
        label: "Procesos de Calidad",
        value: processCounts.calidad,
        note: "Procesos evaluados",
      },
    ]);

    this.y += 7;

    this.addCalloutBox({
      title: "Propósito y alcance",
      text: STATIC_EXECUTIVE_TEXT,
      tone: "neutral",
    });

    this.y += 6;

    const overview = this.parseAiOverview(this.aiOverview);

    this.addSectionLead("Lectura ejecutiva del estado As-Is");

    this.y = this.writeJustifiedParagraph(overview.introduction, this.MARGIN_X, this.y, this.CONTENT_WIDTH, {
      fontSize: 9.6,
      color: COLORS.graphite,
      lineHeight: 1.55,
    });

    if (!overview.available) {
      this.y += 4;
      this.addCalloutBox({
        title: "Nota metodológica",
        text: "El resumen As-Is generado por inteligencia artificial no estaba disponible en el momento de generar el informe. El documento se ha completado con la información estructurada disponible para mantener la trazabilidad del resultado.",
        tone: "warning",
      });
      return;
    }

    this.y += 7;
    this.addInsightColumns(overview.strengths, overview.weaknesses);
  }

  getProcessCountsByDomain(procesos) {
    const counts = {
      gobierno: 0,
      gestion: 0,
      calidad: 0,
    };

    const gobiernoKeywords = [
      "gobierno",
      "política",
      "politica",
      "estrategia",
      "riesgo",
      "riesgos",
      "valor",
      "responsabilidad",
      "responsabilidades",
      "estdat",
      "poldat",
      "valdat",
      "riesdat",
      "estorg",
    ];

    const calidadKeywords = [
      "calidad",
      "aseguramiento",
      "control",
      "monitorización",
      "monitorizacion",
      "mejora de la calidad",
      "planificación de la calidad",
      "planificacion de la calidad",
      "ctrldq",
      "asegdq",
      "plandq",
      "mejdq",
    ];

    safeArray(procesos).forEach((process) => {
      const explicitType = cleanText(process.procesoTipo || process.tipo || process.area || process.ambito).toLowerCase();
      const name = cleanText(process.nombre || process.name || process.codigo || process.id).toLowerCase();
      const combined = `${explicitType} ${name}`;

      if (explicitType.includes("gobierno")) {
        counts.gobierno += 1;
        return;
      }

      if (explicitType.includes("calidad")) {
        counts.calidad += 1;
        return;
      }

      if (explicitType.includes("gestión") || explicitType.includes("gestion")) {
        counts.gestion += 1;
        return;
      }

      if (calidadKeywords.some((keyword) => combined.includes(keyword))) {
        counts.calidad += 1;
        return;
      }

      if (gobiernoKeywords.some((keyword) => combined.includes(keyword))) {
        counts.gobierno += 1;
        return;
      }

      counts.gestion += 1;
    });

    return counts;
  }

  addExecutiveKpiStrip(items) {
    const gap = 6;
    const cardW = (this.CONTENT_WIDTH - gap * 2) / 3;
    const cardH = 27;
    const y = this.y;

    this.checkPageBreak(cardH + 5);

    safeArray(items).forEach((item, index) => {
      const x = this.MARGIN_X + index * (cardW + gap);
      this.setFillColor(COLORS.canvas);
      this.setDrawColor(COLORS.lineSoft);
      this.doc.roundedRect(x, y, cardW, cardH, 2.5, 2.5, "FD");

      this.setFont("normal", 6.8);
      this.setTextColor(COLORS.muted);
      this.doc.text(cleanText(item.label).toUpperCase(), x + 5, y + 7);

      this.setFont("bold", 13.5);
      this.setTextColor(COLORS.executive);
      this.doc.text(String(item.value), x + 5, y + 17);

      this.setFont("normal", 6.8);
      this.setTextColor(COLORS.muted);
      this.doc.text(cleanText(item.note), x + 5, y + 23);
    });

    this.y = y + cardH + 4;
  }

  addSectionLead(title) {
    this.checkPageBreak(13);
    this.setFont("bold", 11.2);
    this.setTextColor(COLORS.executive);
    this.doc.text(title, this.MARGIN_X, this.y);
    this.y += 7;
  }

  addCalloutBox({ title, text, tone = "neutral" }) {
    const toneMap = {
      neutral: { color: COLORS.executive, bg: COLORS.canvas, border: COLORS.line },
      warning: { color: COLORS.warning, bg: COLORS.warningSoft, border: "#E4D2AC" },
      danger: { color: COLORS.danger, bg: COLORS.dangerSoft, border: "#E4B8B8" },
      success: { color: COLORS.success, bg: COLORS.successSoft, border: "#BFDCCB" },
    };

    const cfg = toneMap[tone] || toneMap.neutral;
    const padding = 7;
    const titleHeight = 7;
    const textHeight = this.getTextHeight(text, this.CONTENT_WIDTH - padding * 2 - 3, 8.8, 1.5);
    const height = Math.max(31, padding + titleHeight + textHeight + padding + 2);

    this.checkPageBreak(height + 4);

    const x = this.MARGIN_X;
    const y = this.y;

    this.setFillColor(cfg.bg);
    this.setDrawColor(cfg.border);
    this.doc.roundedRect(x, y, this.CONTENT_WIDTH, height, 2.5, 2.5, "FD");

    this.setFillColor(cfg.color);
    this.doc.rect(x, y, 2.2, height, "F");

    this.setFont("bold", 8.4);
    this.setTextColor(cfg.color);
    this.doc.text(title.toUpperCase(), x + padding, y + 9);

    this.writeJustifiedParagraph(text, x + padding, y + 18, this.CONTENT_WIDTH - padding * 2 - 1, {
      fontSize: 8.8,
      color: COLORS.graphite,
      lineHeight: 1.5,
    });

    this.y = y + height + 4;
  }

  addInsightColumns(strengths, weaknesses) {
    this.addInsightFullList({
      title: "Fortalezas ejecutivas",
      items: strengths,
      emptyText: "No se han recibido fortalezas específicas en el resumen generado.",
      tone: "success",
    });

    this.y += 3;

    this.addInsightFullList({
      title: "Brechas prioritarias",
      items: weaknesses,
      emptyText: "No se han recibido debilidades específicas en el resumen generado.",
      tone: "danger",
    });
  }

  addInsightFullList({ title, items, emptyText, tone }) {
    const cfg =
      tone === "success"
        ? { color: COLORS.success, bg: COLORS.successSoft, border: "#BCD9C8" }
        : { color: COLORS.danger, bg: COLORS.dangerSoft, border: "#E1BDBD" };

    const list = safeArray(items).map(cleanText).filter(Boolean);
    const content = list.length ? list : [emptyText];
    const padding = 6;
    let required = 20;

    content.forEach((item) => {
      required += this.getTextHeight(item, this.CONTENT_WIDTH - 18, 7.8, 1.25) + 3;
    });

    this.checkPageBreak(Math.min(required + 8, 120));

    const x = this.MARGIN_X;
    let y = this.y;

    this.setFillColor(cfg.bg);
    this.setDrawColor(cfg.border);
    this.doc.roundedRect(x, y, this.CONTENT_WIDTH, Math.min(required, this.PAGE_HEIGHT - this.BOTTOM_MARGIN - y), 2.5, 2.5, "FD");
    this.setFillColor(cfg.color);
    this.doc.rect(x, y, 2.2, Math.min(required, this.PAGE_HEIGHT - this.BOTTOM_MARGIN - y), "F");

    this.setFont("bold", 8.5);
    this.setTextColor(cfg.color);
    this.doc.text(title.toUpperCase(), x + padding, y + 8.8);

    y += 17;

    content.forEach((item, index) => {
      const lines = this.doc.splitTextToSize(item, this.CONTENT_WIDTH - 20);
      const needed = lines.length * 3.8 + 4;

      if (y + needed > this.PAGE_HEIGHT - this.BOTTOM_MARGIN) {
        this.addPage();
        y = this.y;
      }

      this.setFont("bold", 7.5);
      this.setTextColor(cfg.color);
      this.doc.text(`${index + 1}.`, x + padding, y);

      this.setFont("normal", 7.8);
      this.setTextColor(COLORS.graphite);
      this.doc.text(lines, x + padding + 7, y, { lineHeightFactor: 1.25 });
      y += lines.length * 3.8 + 3.2;
    });

    this.y = y + 5;
  }

  getInsightBoxHeight(items) {
    const list = safeArray(items);
    if (list.length === 0) return 55;

    let height = 23;
    list.slice(0, 6).forEach((item) => {
      height += this.getTextHeight(item, 70, 7.8, 1.35) + 4;
    });

    return Math.min(Math.max(height, 60), 122);
  }

  drawInsightBox({ x, y, width, height, title, items, emptyText, tone }) {
    const cfg =
      tone === "success"
        ? { color: COLORS.success, bg: COLORS.successSoft, border: "#BCD9C8" }
        : { color: COLORS.danger, bg: COLORS.dangerSoft, border: "#E1BDBD" };

    this.setFillColor(cfg.bg);
    this.setDrawColor(cfg.border);
    this.doc.roundedRect(x, y, width, height, 2.5, 2.5, "FD");

    this.setFillColor(cfg.color);
    this.doc.rect(x, y, 2, height, "F");

    this.setFont("bold", 8.3);
    this.setTextColor(cfg.color);
    this.doc.text(title.toUpperCase(), x + 6, y + 8.5);

    this.setDrawColor(cfg.border);
    this.doc.line(x + 6, y + 13, x + width - 6, y + 13);

    const list = safeArray(items);
    let itemY = y + 21;

    if (list.length === 0) {
      this.writeJustifiedParagraph(emptyText, x + 6, itemY, width - 12, {
        fontSize: 7.8,
        color: COLORS.graphite,
        lineHeight: 1.35,
      });
      return;
    }

    list.slice(0, 6).forEach((item, index) => {
      const content = cleanText(item);
      const lines = this.doc.splitTextToSize(content, width - 17);

      if (itemY + lines.length * 4 > y + height - 5) return;

      this.setFont("bold", 7.4);
      this.setTextColor(cfg.color);
      this.doc.text(`${index + 1}.`, x + 6, itemY);

      this.setFont("normal", 7.7);
      this.setTextColor(COLORS.graphite);
      this.doc.text(lines, x + 12, itemY, { lineHeightFactor: 1.32 });

      itemY += lines.length * 4.1 + 3;
    });

    if (list.length > 6) {
      this.setFont("italic", 7.2);
      this.setTextColor(COLORS.muted);
      this.doc.text(`+ ${list.length - 6} elementos adicionales`, x + 6, y + height - 5);
    }
  }

  addGraphicOverviewSection() {
    this.startSection("2. Visión general gráfica", "resultados visuales");

    const intro =
      "Esta sección presenta una visión visual del nivel de capacidad alcanzado en los procesos evaluados. Su objetivo es facilitar una lectura rápida de concentración de brechas, áreas con mayor avance y ámbitos que requieren refuerzo metodológico u operativo.";

    this.y = this.writeJustifiedParagraph(intro, this.MARGIN_X, this.y, this.CONTENT_WIDTH, {
      fontSize: 9.6,
      color: COLORS.graphite,
      lineHeight: 1.5,
    });

    this.y += 5;

    const charts = [
      { title: "Gobierno del Dato", data: this.chartImages?.gobierno },
      { title: "Gestión del Dato", data: this.chartImages?.gestion },
      { title: "Calidad del Dato", data: this.chartImages?.calidad },
    ].filter((chart) => chart.data?.dataUrl && chart.data?.width && chart.data?.height);

    if (charts.length === 0) {
      this.addCalloutBox({
        title: "Gráficas no disponibles",
        text: "No se han recibido imágenes de gráficas para esta generación. El informe continúa con los resultados tabulares y el plan de mejora disponible.",
        tone: "warning",
      });
      return;
    }

    charts.forEach((chart) => {
      const originalWidth = Number(chart.data.width);
      const originalHeight = Number(chart.data.height);

      const boxH = 102;
      this.checkPageBreak(boxH + 12);

      const x = this.MARGIN_X;
      const y = this.y;

      this.setFillColor(COLORS.paper);
      this.setDrawColor(COLORS.lineSoft);
      this.doc.roundedRect(x, y, this.CONTENT_WIDTH, boxH, 2.5, 2.5, "FD");

      this.setFont("bold", 9.5);
      this.setTextColor(COLORS.executive);
      this.doc.text(chart.title.toUpperCase(), x + 7, y + 10);

      this.setDrawColor(COLORS.lineSoft);
      this.doc.line(x + 7, y + 15, x + this.CONTENT_WIDTH - 7, y + 15);

      const imageWidth = this.CONTENT_WIDTH * 0.68;
      const imageHeight = Math.min((originalHeight / originalWidth) * imageWidth, 74);
      const imageX = x + (this.CONTENT_WIDTH - imageWidth) / 2;
      const imageY = y + 22;

      try {
        this.doc.addImage(chart.data.dataUrl, "PNG", imageX, imageY, imageWidth, imageHeight);
      } catch (error) {
        this.setFont("normal", 8);
        this.setTextColor(COLORS.muted);
        this.doc.text("No se pudo insertar esta gráfica en el PDF.", x + 7, y + 30);
      }

      this.y = y + boxH + 10;
    });
  }

  addConsolidatedResultsSection() {
    this.startSection("3. Cálculo de niveles de capacidad", "capacidad según UNE 0080");

    const intro =
      "Los valores muestran el grado de logro de los atributos evaluados por nivel. La capacidad se interpreta conforme a los tramos de logro definidos en UNE 0080, diferenciando el nivel específico del proceso y los niveles generales de capacidad.";

    this.y = this.writeJustifiedParagraph(intro, this.MARGIN_X, this.y, this.CONTENT_WIDTH, {
      fontSize: 9.4,
      color: COLORS.graphite,
      lineHeight: 1.48,
    });

    this.y += 5;
    this.addAchievementLegend();

    const procesos = safeArray(this.data.resultadosPorProceso);

    if (procesos.length === 0) {
      this.addCalloutBox({
        title: "Sin procesos evaluados",
        text: "No se han recibido resultados por proceso para incluir en esta sección.",
        tone: "warning",
      });
      return;
    }

    const levelKeys = Array.from(
      new Set(
        procesos.flatMap((process) =>
          Object.keys(process.averageScoresPerLevel || {}).map(String)
        )
      )
    ).sort((a, b) => Number(a) - Number(b));

    const head = [[
      "Proceso",
      "Nivel de capacidad",
      ...levelKeys.map((level) =>
        level === "1" ? "Nivel 1 específico" : `Nivel ${level} general`
      ),
    ]];

    const body = procesos.map((process) => {
      const processLabel = [
        cleanText(process.nombre || "Proceso sin nombre"),
        cleanText(process.abreviatura),
      ]
        .filter(Boolean)
        .join("\n");

      return [
        processLabel,
        process.nivelCapacidad !== undefined && process.nivelCapacidad !== null
          ? `Nivel ${process.nivelCapacidad}`
          : "-",
        ...levelKeys.map((level) => getLevelStatus(process.averageScoresPerLevel?.[level]).text),
      ];
    });

    const availableWidth = this.CONTENT_WIDTH;
    const processWidth = 54;
    const capacityWidth = 27;
    const levelWidth = Math.max(21, (availableWidth - processWidth - capacityWidth) / Math.max(1, levelKeys.length));

    autoTable(this.doc, {
      head,
      body,
      startY: this.y,
      margin: { top: this.TOP_MARGIN, left: this.MARGIN_X, right: this.MARGIN_X },
      theme: "plain",
      headStyles: {
        fillColor: hexToRgb("#F8FAFC"),
        textColor: hexToRgb(COLORS.executive),
        font: this.FONT,
        fontStyle: "bold",
        fontSize: 7.4,
        halign: "center",
        valign: "middle",
        cellPadding: 3,
        lineColor: hexToRgb(COLORS.lineSoft),
        lineWidth: 0.15,
      },
      styles: {
        font: this.FONT,
        fontSize: 7.1,
        cellPadding: 2.6,
        textColor: hexToRgb(COLORS.graphite),
        lineColor: hexToRgb(COLORS.lineSoft),
        lineWidth: 0.12,
        valign: "middle",
        overflow: "linebreak",
      },
      alternateRowStyles: { fillColor: hexToRgb("#FBFCFE") },
      columnStyles: {
        0: { cellWidth: processWidth, halign: "left", fontStyle: "bold", textColor: hexToRgb(COLORS.executive) },
        1: { cellWidth: capacityWidth, halign: "center", fontStyle: "bold", textColor: hexToRgb(COLORS.white), fillColor: hexToRgb(COLORS.steel) },
        ...Object.fromEntries(
          levelKeys.map((_, index) => [
            index + 2,
            { cellWidth: levelWidth, halign: "center", fontStyle: "bold" },
          ])
        ),
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index >= 2) {
          const text = String(data.cell.raw || "");
          const match = text.match(/\((N|P|A|T)\)/);
          if (match) {
            data.cell.styles.textColor = hexToRgb(LEVEL_COLORS[match[1]]);
            data.cell.styles.fontStyle = "bold";
          }
        }

        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    this.y = this.doc.lastAutoTable.finalY + 10;
  }

  addAchievementLegend() {
    this.checkPageBreak(48);

    const x = this.MARGIN_X;
    const y = this.y;
    const height = 43;
    const padding = 6;
    const gap = 5;
    const itemW = (this.CONTENT_WIDTH - padding * 2 - gap) / 2;
    const itemH = 12;

    this.setFillColor(COLORS.paper);
    this.setDrawColor(COLORS.lineSoft);
    this.doc.roundedRect(x, y, this.CONTENT_WIDTH, height, 2.8, 2.8, "FD");

    this.setFillColor(COLORS.executive);
    this.doc.rect(x, y, 2.2, height, "F");

    this.setFont("bold", 8.3);
    this.setTextColor(COLORS.executive);
    this.doc.text("LEYENDA DE LOGRO DE ATRIBUTOS", x + padding, y + 8.5);

    this.setFont("normal", 6.8);
    this.setTextColor(COLORS.muted);
    this.doc.text("Criterio de lectura N / P / A / T", this.PAGE_WIDTH - this.MARGIN_X - padding, y + 8.5, {
      align: "right",
    });

    const items = [
      ["N", "No logrado", "<= 15%"],
      ["P", "Parcialmente logrado", "> 15% y <= 50%"],
      ["A", "Ampliamente logrado", "> 50% y <= 85%"],
      ["T", "Totalmente logrado", "> 85%"],
    ];

    items.forEach(([letter, label, range], index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const itemX = x + padding + col * (itemW + gap);
      const itemY = y + 15 + row * 13.5;

      this.setFillColor(COLORS.canvas);
      this.setDrawColor(COLORS.lineSoft);
      this.doc.roundedRect(itemX, itemY, itemW, itemH, 2, 2, "FD");

      this.setFillColor(LEVEL_COLORS[letter]);
      this.doc.roundedRect(itemX + 3, itemY + 2.2, 8.5, 7.5, 1.5, 1.5, "F");

      this.setFont("bold", 7.1);
      this.setTextColor(COLORS.white);
      this.doc.text(letter, itemX + 7.25, itemY + 7.5, { align: "center" });

      this.setFont("bold", 7.4);
      this.setTextColor(COLORS.graphite);
      this.doc.text(label, itemX + 15, itemY + 5.1);

      this.setFont("normal", 6.7);
      this.setTextColor(COLORS.muted);
      this.doc.text(range, itemX + 15, itemY + 9.4);
    });

    this.y += height + 10;
  }

  addDetailedProcessSection() {
    this.startSection("4. Análisis detallado por proceso", "preguntas específicas");

    const procesos = safeArray(this.data.resultadosPorProceso);
    const procesosConPreguntas = procesos.filter((process) => safeArray(process.preguntas).length > 0);

    if (procesosConPreguntas.length === 0) {
      this.addCalloutBox({
        title: "Sin preguntas específicas",
        text: "No se han recibido preguntas específicas por proceso para incluir en esta sección.",
        tone: "warning",
      });
      return;
    }

    const head = [["ID", "Pregunta específica", "Resultado"]];

    procesosConPreguntas.forEach((process) => {
      this.checkPageBreak(31);

      this.setFont("bold", 10.8);
      this.setTextColor(COLORS.executive);
      const titleLines = this.doc.splitTextToSize(cleanText(process.nombre || "Proceso sin nombre"), this.CONTENT_WIDTH);
      this.doc.text(titleLines, this.MARGIN_X, this.y, { lineHeightFactor: 1.25 });
      this.y += titleLines.length * 5 + 3;

      this.setDrawColor(COLORS.champagne);
      this.doc.setLineWidth(0.35);
      this.doc.line(this.MARGIN_X, this.y, this.MARGIN_X + 22, this.y);
      this.y += 5;

      const body = safeArray(process.preguntas).map((question, index) => [
        cleanText(question.id || question.codigo || index + 1),
        cleanText(question.texto || question.pregunta || question.descripcion || "Pregunta sin texto"),
        question.porcentaje !== undefined && question.porcentaje !== null ? `${question.porcentaje}%` : "-",
      ]);

      autoTable(this.doc, {
        head,
        body,
        startY: this.y,
        margin: { top: this.TOP_MARGIN, left: this.MARGIN_X, right: this.MARGIN_X },
        theme: "plain",
        headStyles: {
          fillColor: hexToRgb(COLORS.blueGray),
          textColor: hexToRgb(COLORS.executive),
          font: this.FONT,
          fontStyle: "bold",
          fontSize: 7.5,
          cellPadding: 2.5,
        },
        styles: {
          font: this.FONT,
          fontSize: 7.35,
          cellPadding: 2.5,
          textColor: hexToRgb(COLORS.graphite),
          lineColor: hexToRgb(COLORS.lineSoft),
          lineWidth: 0.12,
          overflow: "linebreak",
        },
        alternateRowStyles: { fillColor: hexToRgb(COLORS.canvas) },
        columnStyles: {
          0: { cellWidth: 22, fontStyle: "bold", halign: "left", textColor: hexToRgb(COLORS.executive) },
          1: { cellWidth: "auto" },
          2: { cellWidth: 23, halign: "center", fontStyle: "bold" },
        },
      });

      this.y = this.doc.lastAutoTable.finalY + 12;
    });
  }

  addGeneralQuestionsSection() {
    this.startSection("5. Análisis de preguntas generales", "atributos superiores");

    const preguntasGenerales = this.data.preguntasGenerales || {};
    const levels = ["2", "3", "4", "5"].filter(
      (level) => safeArray(preguntasGenerales?.[level]?.preguntas).length > 0
    );

    if (levels.length === 0) {
      this.addCalloutBox({
        title: "Sin preguntas generales",
        text: "No se han recibido preguntas generales de niveles superiores para incluir en esta sección.",
        tone: "warning",
      });
      return;
    }

    const head = [["ID", "Pregunta organizacional", "Resultado"]];

    levels.forEach((level) => {
      const data = preguntasGenerales[level];
      const promedio =
        data?.promedioPorcentaje !== undefined && data?.promedioPorcentaje !== null
          ? `Promedio ${Math.round(data.promedioPorcentaje)}%`
          : "Promedio no disponible";

      this.checkPageBreak(32);

      this.setFillColor(COLORS.executive);
      this.doc.roundedRect(this.MARGIN_X, this.y, this.CONTENT_WIDTH, 12, 2, 2, "F");

      this.setFont("bold", 8.2);
      this.setTextColor(COLORS.white);
      this.doc.text(`NIVEL ${level}: PREGUNTAS GENERALES`, this.MARGIN_X + 5, this.y + 7.8);

      this.setFont("normal", 7.5);
      this.setTextColor("#C9D4E2");
      this.doc.text(promedio, this.PAGE_WIDTH - this.MARGIN_X - 5, this.y + 7.8, { align: "right" });

      this.y += 15;

      const body = safeArray(data.preguntas).map((question, index) => [
        cleanText(question.id || question.codigo || index + 1),
        cleanText(question.texto || question.pregunta || question.descripcion || "Pregunta sin texto"),
        question.porcentaje !== undefined && question.porcentaje !== null ? `${question.porcentaje}%` : "-",
      ]);

      autoTable(this.doc, {
        head,
        body,
        startY: this.y,
        margin: { top: this.TOP_MARGIN, left: this.MARGIN_X, right: this.MARGIN_X },
        theme: "plain",
        headStyles: {
          fillColor: hexToRgb(COLORS.blueGray),
          textColor: hexToRgb(COLORS.executive),
          font: this.FONT,
          fontStyle: "bold",
          fontSize: 7.5,
          cellPadding: 2.5,
        },
        styles: {
          font: this.FONT,
          fontSize: 7.35,
          cellPadding: 2.5,
          textColor: hexToRgb(COLORS.graphite),
          lineColor: hexToRgb(COLORS.lineSoft),
          lineWidth: 0.12,
          overflow: "linebreak",
        },
        alternateRowStyles: { fillColor: hexToRgb(COLORS.canvas) },
        columnStyles: {
          0: { cellWidth: 22, fontStyle: "bold", textColor: hexToRgb(COLORS.executive) },
          1: { cellWidth: "auto" },
          2: { cellWidth: 23, halign: "center", fontStyle: "bold" },
        },
      });

      this.y = this.doc.lastAutoTable.finalY + 13;
    });
  }

  parseTaskDescription(description) {
    const raw = cleanText(description);

    if (isBadGeneratedDescription(raw)) {
      return { summary: "", steps: [], priority: "", impact: "" };
    }

    try {
      const parsed = JSON.parse(raw);

      if (parsed && typeof parsed === "object") {
        return {
          summary: cleanText(parsed.breve || parsed.descripcion || parsed.resumen || parsed.info || ""),
          steps: safeArray(parsed.pasos || parsed.steps).map(cleanText).filter(Boolean),
          priority: cleanText(parsed.prioridad || parsed.priority || ""),
          impact: cleanText(parsed.impacto || parsed.impact || ""),
        };
      }
    } catch (error) {
      // Continue with plain text parser
    }

    const lines = raw.split("\n").map((line) => cleanText(line)).filter(Boolean);

    const summary = [];
    const steps = [];
    let priority = "";
    let impact = "";
    let inSteps = false;

    lines.forEach((line) => {
      const lower = line.toLowerCase();

      if (lower.startsWith("pasos")) {
        inSteps = true;
        return;
      }

      if (lower.includes("prioridad:")) {
        priority = cleanText(line.substring(lower.indexOf("prioridad:") + 10));
        return;
      }

      if (lower.includes("impacto:")) {
        impact = cleanText(line.substring(lower.indexOf("impacto:") + 8));
        return;
      }

      if (/^\d+[.)]\s*/.test(line)) {
        inSteps = true;
        steps.push(cleanText(line.replace(/^\d+[.)]\s*/, "")));
        return;
      }

      if (inSteps && steps.length > 0) {
        steps[steps.length - 1] = `${steps[steps.length - 1]} ${line}`;
      } else {
        summary.push(line.replace(/^Descripción:\s*/i, ""));
      }
    });

    return { summary: cleanText(summary.join(" ")), steps, priority, impact };
  }

  addImprovementPlanSection() {
    this.startSection("6. Plan de mejora detallado", "acciones recomendadas");

    const validTasks = this.tasks.filter((task) => cleanText(this.getTaskTitleForPdf(task)));

    if (validTasks.length === 0) {
      this.addCalloutBox({
        title: "Sin tareas de mejora",
        text: "No se han identificado tareas de mejora asociadas a la autoevaluación.",
        tone: "warning",
      });
      return;
    }

    const intro =
      "El plan de mejora se presenta por horizonte temporal para facilitar la ejecución. Cada tarea incluye su descripción, pasos recomendados, evidencias esperadas y perfiles implicados, de forma que pueda trasladarse directamente a una planificación operativa.";

    this.y = this.writeJustifiedParagraph(intro, this.MARGIN_X, this.y, this.CONTENT_WIDTH, {
      fontSize: 9.2,
      color: COLORS.graphite,
      lineHeight: 1.35,
    });

    this.y += 4;

    const horizonConfig = [
      { label: "Corto plazo", key: "Corto plazo", color: COLORS.danger },
      { label: "Medio plazo", key: "Medio plazo", color: COLORS.steel },
      { label: "Largo plazo", key: "Largo plazo", color: COLORS.success },
    ];

    const grouped = horizonConfig.map((horizon) => ({
      ...horizon,
      tasks: validTasks.filter((task) => cleanText(task.plazo || task.plazoBase || "Medio plazo").toLowerCase().includes(horizon.key.toLowerCase().split(" ")[0])),
    }));

    grouped.forEach((group) => {
      if (!group.tasks.length) return;

      this.checkPageBreak(28);
      this.setFont("bold", 12);
      this.setTextColor(group.color);
      this.doc.text(`${group.label.toUpperCase()} · ${group.tasks.length} acciones`, this.MARGIN_X, this.y);
      this.y += 4;
      this.drawThinRule(this.y, COLORS.lineSoft);
      this.y += 7;

      group.tasks.forEach((task, index) => {
        this.addTaskCard(task, index + 1, group.color);
      });

      this.y += 4;
    });
  }

  getTaskTitleForPdf(task) {
    return getTaskTitleForMatching(task) || "Tarea sin título";
  }

  cleanOperationalDescription(description) {
    let value = cleanText(description);

    if (!value || isBadGeneratedDescription(value)) return "";

    const sectionMarkers = [
      /\bPasos\s+de\s+implantaci[oó]n\b/i,
      /\bPasos\s*:/i,
      /\bEvidencias\s+esperadas\b/i,
      /\bEvidencias\s*:/i,
      /\bResponsables\s+sugeridos\b/i,
      /\bImplicados\s+sugeridos\b/i,
      /\bPrioridad\s*:/i,
    ];

    const cutPositions = sectionMarkers
      .map((regex) => {
        const match = value.match(regex);
        return match ? match.index : -1;
      })
      .filter((index) => index >= 0);

    if (cutPositions.length) {
      value = value.slice(0, Math.min(...cutPositions));
    }

    value = value
      .replace(/\s*Pasos?\s+\d+\. .*$/i, "")
      .replace(/\s*Evidencias?\s+esperadas?.*$/i, "")
      .replace(/\s*Responsables?\s+sugeridos?.*$/i, "")
      .replace(/\s*Prioridad\s*:\s*(Alta|Media|Baja).*$/i, "")
      .replace(/\s+/g, " ")
      .trim();

    return value;
  }

  getTaskDescriptionForPdf(task) {
    const direct = this.cleanOperationalDescription(task?.descripcion);
    if (direct) return direct;

    const parsed = this.parseTaskDescription(task?.descripcionCompleta || task?.descripcion);
    const parsedSummary = this.cleanOperationalDescription(parsed.summary);

    return parsedSummary ||
      "Acción recomendada identificada durante la autoevaluación. Se recomienda revisar su alcance, asignar responsable, definir criterios de aceptación y concretar la planificación antes de su implantación.";
  }

  renderSubsectionTitle(title, x, y) {
    this.setFont("bold", 7.8);
    this.setTextColor(COLORS.steel);
    this.doc.text(title, x, y);
    return y + 4.5;
  }

  renderNumberedList(items, x, y, width) {
    let cursorY = y;
    safeArray(items).map(cleanText).filter(Boolean).forEach((item, index) => {
      const numberWidth = 7;
      const lines = this.doc.splitTextToSize(item, width - numberWidth - 2);
      const needed = Math.max(5.8, lines.length * 3.7 + 1.4);

      if (cursorY + needed > this.PAGE_HEIGHT - this.BOTTOM_MARGIN) {
        this.addPage();
        cursorY = this.y;
      }

      this.setFont("bold", 7.25);
      this.setTextColor(COLORS.executive);
      this.doc.text(`${index + 1}.`, x, cursorY);

      this.setFont("normal", 7.35);
      this.setTextColor(COLORS.graphite);
      this.doc.text(lines, x + numberWidth, cursorY, { lineHeightFactor: 1.22 });
      cursorY += lines.length * 3.75 + 2;
    });

    return cursorY;
  }

  renderBulletList(items, x, y, width) {
    let cursorY = y;
    safeArray(items).map(cleanText).filter(Boolean).forEach((item) => {
      const lines = this.doc.splitTextToSize(item, width - 7);
      const needed = Math.max(5.5, lines.length * 3.55 + 1.5);

      if (cursorY + needed > this.PAGE_HEIGHT - this.BOTTOM_MARGIN) {
        this.addPage();
        cursorY = this.y;
      }

      this.setFillColor(COLORS.steel);
      this.doc.circle(x + 1.4, cursorY - 1.2, 0.7, "F");

      this.setFont("normal", 7.25);
      this.setTextColor(COLORS.graphite);
      this.doc.text(lines, x + 6, cursorY, { lineHeightFactor: 1.18 });
      cursorY += lines.length * 3.55 + 2;
    });

    return cursorY;
  }

  renderInlineChips(items, x, y, width) {
    const values = safeArray(items).map(cleanText).filter(Boolean);
    if (!values.length) return y;

    let cursorX = x;
    let cursorY = y;
    const chipH = 6.4;

    values.forEach((value) => {
      const chipW = Math.min(width, this.doc.getTextWidth(value) + 8);

      if (cursorX + chipW > x + width) {
        cursorX = x;
        cursorY += chipH + 2;
      }

      if (cursorY + chipH > this.PAGE_HEIGHT - this.BOTTOM_MARGIN) {
        this.addPage();
        cursorY = this.y;
        cursorX = x;
      }

      this.setFillColor(COLORS.blueGray);
      this.setDrawColor(COLORS.lineSoft);
      this.doc.roundedRect(cursorX, cursorY - 4.5, chipW, chipH, 1.8, 1.8, "FD");

      this.setFont("bold", 6.4);
      this.setTextColor(COLORS.steel);
      this.doc.text(value, cursorX + 4, cursorY, { maxWidth: chipW - 7 });
      cursorX += chipW + 3;
    });

    return cursorY + chipH + 2;
  }

  addTaskCard(task, number, accentColor = COLORS.executive) {
    const title = this.getTaskTitleForPdf(task);
    const summaryText = this.getTaskDescriptionForPdf(task);
    const parsed = this.parseTaskDescription(task?.descripcionCompleta || task?.descripcion);
    const steps = (safeArray(task?.pasos).length ? safeArray(task?.pasos) : safeArray(parsed.steps)).map(cleanText).filter(Boolean);
    const evidences = safeArray(task?.evidencias || task?.evidenciasEsperadas).map(cleanText).filter(Boolean);
    const responsibles = safeArray(task?.responsablesSugeridos || task?.responsables || task?.implicados || task?.implicadosSugeridos).map(cleanText).filter(Boolean);
    const processName = cleanText(task?.procesoNombre || task?.proceso || "Proceso no identificado");

    this.checkPageBreak(42);

    const x = this.MARGIN_X;
    const width = this.CONTENT_WIDTH;

    this.setDrawColor(COLORS.lineSoft);
    this.doc.setLineWidth(0.25);
    this.doc.line(x, this.y, x + width, this.y);
    this.y += 5.5;

    this.setFillColor(accentColor);
    this.doc.rect(x, this.y - 1.5, 1.4, 12, "F");

    this.setFont("bold", 7.4);
    this.setTextColor(COLORS.muted);
    this.doc.text(`Acción ${number}`, x + 5, this.y + 2.2);

    this.setFont("bold", 9.4);
    this.setTextColor(COLORS.executive);
    const titleLines = this.doc.splitTextToSize(title, width - 8);
    this.doc.text(titleLines, x + 5, this.y + 8, { lineHeightFactor: 1.2 });
    this.y += 8 + titleLines.length * 4.5 + 1.8;

    this.setFont("italic", 7.2);
    this.setTextColor(COLORS.muted);
    this.doc.text(processName, x + 5, this.y, { maxWidth: width - 10 });
    this.y += 6;

    this.y = this.renderSubsectionTitle("Descripción", x + 5, this.y);
    this.y = this.writeJustifiedParagraph(summaryText, x + 5, this.y, width - 10, {
      fontSize: 7.75,
      color: COLORS.graphite,
      lineHeight: 1.36,
    });

    if (steps.length) {
      this.y += 2;
      this.y = this.renderSubsectionTitle("Pasos de implantación", x + 5, this.y);
      this.y = this.renderNumberedList(steps, x + 5, this.y, width - 10);
    }

    if (evidences.length) {
      this.y += 1.5;
      this.y = this.renderSubsectionTitle("Evidencias esperadas", x + 5, this.y);
      this.y = this.renderBulletList(evidences, x + 5, this.y, width - 10);
    }

    if (responsibles.length) {
      this.y += 1.5;
      this.y = this.renderSubsectionTitle("Implicados sugeridos", x + 5, this.y);
      this.y = this.renderBulletList(responsibles, x + 5, this.y, width - 10);
    }

    this.y += 5;
  }

 addClosingPage() {
  this.startSection("7. Cierre del informe", "siguientes pasos");

  const text =
    "Este informe recoge los resultados de una autoevaluación realizada a partir de las respuestas proporcionadas por la propia organización. Por tanto, debe interpretarse como una herramienta orientativa de diagnóstico y planificación, útil para identificar posibles brechas, ordenar iniciativas de mejora y facilitar la toma de decisiones en materia de gobierno, gestión y calidad del dato. Las acciones recomendadas se proponen en función de la información declarada durante la autoevaluación y deben ser contrastadas internamente antes de su implantación.";

  this.y = this.writeJustifiedParagraph(text, this.MARGIN_X, this.y, this.CONTENT_WIDTH, {
    fontSize: 9.8,
    color: COLORS.graphite,
    lineHeight: 1.55,
  });

  this.y += 7;

  const text2 =
    "Para obtener una valoración profesional, objetiva y plenamente verificable del nivel de capacidad y madurez de los procesos de datos, se recomienda someter los resultados a un proceso de auditoría profesional. Dicha auditoría debería incluir revisión documental, entrevistas con las áreas implicadas, contraste de evidencias, análisis de la implantación real de las prácticas declaradas y validación independiente de los hallazgos.";

  this.y = this.writeJustifiedParagraph(text2, this.MARGIN_X, this.y, this.CONTENT_WIDTH, {
    fontSize: 9.8,
    color: COLORS.graphite,
    lineHeight: 1.55,
  });

  this.y += 7;

  this.addCalloutBox({
    title: "Recomendación final",
    text: "Las mejoras propuestas en este informe se basan exclusivamente en las respuestas introducidas en la autoevaluación. Por ello, no constituyen una garantía absoluta del estado real de la organización ni sustituyen a una auditoría formal. Se recomienda validar cada recomendación con evidencias objetivas, responsables internos y un plan operativo que incluya responsables, fechas objetivo, dependencias e indicadores de seguimiento.",
    tone: "neutral",
  });

  this.y += 12;

  this.setFont("bold", 10);
  this.setTextColor(COLORS.executive);
  this.doc.text("DQPA", this.MARGIN_X, this.y);

  this.y += 6;

  this.setFont("normal", 8);
  this.setTextColor(COLORS.muted);
  this.doc.text(
    "Informe de autoevaluación generado como soporte orientativo para la toma de decisiones y la planificación de mejoras.",
    this.MARGIN_X,
    this.y
  );
}
}

const enrichTasksSafely = async ({ tasks, cuestionarioId, token }) => {
  const catalogTasks = safeArray(tasks).map((task) => ({
    ...task,
    titulo: getTaskTitleForMatching(task) || task?.titulo,
    tarea: getTaskTitleForMatching(task) || task?.tarea,
  }));

  // El PDF reutiliza el roadmap local del backend para conocer el horizonte temporal,
  // pero NO pierde el contenido premium que ya viene en /all-tasks.
  // Por eso fusionamos cada tarea del roadmap con su ficha del catálogo detallado.
  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.organizarPorPlazo(cuestionarioId),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const grouped = [
      ...safeArray(response.data?.cortoPlazo).map((task) => ({ ...task, plazo: "Corto plazo" })),
      ...safeArray(response.data?.medioPlazo).map((task) => ({ ...task, plazo: "Medio plazo" })),
      ...safeArray(response.data?.largoPlazo).map((task) => ({ ...task, plazo: "Largo plazo" })),
    ];

    if (grouped.length === 0) return catalogTasks;

    return grouped.map((roadmapTask) => mergeRoadmapTaskWithCatalog(roadmapTask, catalogTasks));
  } catch (error) {
    return catalogTasks;
  }
};

const GeneradorPDF = ({ cuestionarioId, chartImages, onComplete, onError }) => {
  const generatedRef = useRef(false);

  useEffect(() => {
    const generatePDF = async () => {
      if (generatedRef.current) return;
      generatedRef.current = true;

      try {
        const token = getAuthToken();

        if (!token) {
          throw new Error("No se encontró el token de autenticación.");
        }

        const detailedPromise = axiosInstance.get(
          API_ENDPOINTS.resultadosDetallados(cuestionarioId),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const aiPromise = axiosInstance
          .post(
            API_ENDPOINTS.generarResumenEjecutivo(cuestionarioId),
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .catch(() => null);

        const tasksPromise = axiosInstance
          .get(API_ENDPOINTS.todasLasTareas(cuestionarioId), {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .catch(() => null);

        const [detailedResponse, aiResponse, tasksResponse] = await Promise.all([
          detailedPromise,
          aiPromise,
          tasksPromise,
        ]);

        const detailedData = detailedResponse?.data || {};
        const aiOverview = aiResponse?.data?.overviewText || "";
        let tasks = safeArray(tasksResponse?.data);

        tasks = await enrichTasksSafely({ tasks, cuestionarioId, token });

        const doc = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
          compress: true,
        });

        const builder = new PDFBuilder(doc, detailedData, aiOverview, chartImages, tasks);
        builder.generate();

        doc.save(`Informe-Ejecutivo-DQPA-${cuestionarioId}.pdf`);

        if (onComplete) onComplete();
      } catch (error) {
        console.error("Error crítico al generar el informe PDF:", error);

        if (onError) {
          onError(error);
        }
      }
    };

    generatePDF();
  }, [cuestionarioId, chartImages, onComplete, onError]);

  return null;
};

export default GeneradorPDF;
